<template>
  <component
    :is="detailsComponent"
    v-if="localData"
    class="product-details"
  >
    <div class="product-details__tile-header">
      <div
        v-if="title || contentsData"
        class="product-details__heading-row product-details__heading-row--align-start"
      >
        <h2
          v-if="title"
          class="text-heading-m text-heading-l-desktop"
          v-html="title"
        />

        <btn
          v-if="contentsData"
          :aria-expanded="isActiveOverlay('ContentsData')"
          aria-haspopup="dialog"
          class-label="
          product-details__text-grey
          text-body-s-underline
          text-body-m-regular-underline
        "
          :label="$string('product.section_titles.common_issues_cta')"
          modifiers="text"
          type="button"
          @click="handleOpenOverlay('ContentsData')"
        />
      </div>

      <p
        v-if="subheading"
        class="product-details__text-grey text-body-s-regular"
        v-html="subheading"
      />
    </div>

    <ul
      class="product-details__columns"
      :class="`product-details__columns--${numColumns}`"
    >
      <template
        v-for="(item, index) of localData"
        :key="`product-detail-${namespace}-${index}`"
      >
        <li
          v-if="item.content || item.circleValues || item.compatibility"
          :class="{
            'product-details__tile--padding-large': isPaddingLarge,
            'product-details__tile': !isTileButton
          }"
        >
          <template v-if="isTileButton">
            <btn
              v-if="item.content || item.compatibility"
              :aria-expanded="isActiveOverlay(index)"
              aria-haspopup="dialog"
              class="product-details__tile"
              class-label="text-body-s-regular text-body-m-regular-desktop"
              :label="item.heading"
              modifiers="text"
              show-icon
              type="button"
              @click="handleOpenOverlay(index, item)"
            />
          </template>

          <template v-else>
            <span
              v-if="item.icon"
              aria-hidden="true"
              class="product-details__icon-red"
            >
              <component :is="getIcon(item.icon)" />
            </span>

            <div class="product-details__tile-content">
              <div class="product-details__heading-row">
                <strong
                  v-if="item.heading"
                  class="text-body-s-bold text-body-m-bold-desktop"
                  v-html="item.heading"
                />

                <btn
                  v-if="item.content"
                  :aria-expanded="isActiveOverlay(index)"
                  aria-haspopup="dialog"
                  class="product-details__learn-more"
                  class-label="text-body-xs-underline"
                  :label="learnMoreString(item)"
                  modifiers="text"
                  type="button"
                  @click="handleOpenOverlay(index)"
                />
              </div>

              <span
                v-if="item.subheading"
                class="product-details__subheading product-details__text-grey"
                v-html="item.subheading"
              />

              <ul
                v-if="item.circleValues?.length > 0"
                class="product-details__circle-values-container"
              >
                <li
                  v-for="(value, circleIndex) of item.circleValues"
                  :key="`${namespace}-${value}-${circleIndex}`"
                  class="product-details__circle-value"
                >
                  <span
                    aria-hidden="true"
                    class="product-details__circle-value-icon"
                  >
                    <span
                      class="
                        product-details__progress-icon
                        product-details__icon-red
                      "
                    >
                      <component
                        :is="`icon-${getProgressIcon(value)}`"
                        aria-hidden="true"
                      />
                    </span>

                    <component
                      :is="`icon-${getCircleInfo(circleIndex)?.icon}`"
                      aria-hidden="true"
                    />
                  </span>

                  <span class="product-details__circle-value-text">
                    <strong
                      class="product-details__text-grey text-body-xs-bold"
                      v-text="getCircleInfo(circleIndex)?.title"
                    />

                    <span
                      class="product-details__text-grey text-body-xs-regular"
                      v-text="value"
                    />
                  </span>
                </li>
              </ul>
            </div>
          </template>

          <overlay
            v-if="item.content"
            :key="`productDetail${namespace}${index}`"
            block-class="product-details"
            direction="right"
            :namespace="`productDetail${namespace}${index}`"
            type="drawer"
          >
            <template #title>
              <span v-html="item.heading" />
            </template>

            <template #body>
              <rte-content
                :content="item.content"
                font-size="s"
                is-overlay
              />
            </template>
          </overlay>
        </li>
      </template>
    </ul>

    <overlay
      v-if="contentsData"
      :key="`productDetail${namespace}ContentsData`"
      block-class="product-details"
      direction="right"
      :namespace="`productDetail${namespace}ContentsData`"
      type="drawer"
    >
      <template #title>
        <span v-html="contentsData.heading" />
      </template>

      <template #body>
        <rte-content
          :content="contentsData.content"
          font-size="s"
          is-overlay
        />
      </template>
    </overlay>
  </component>
</template>

<script>

/**
 * Vue: Product details (product-details)
 * -----------------------------------------------------------------------------
 * Renders columns of product details powered by variant or product metafields,
 * with some tiles opening drawers.
 *
 * @param {Array} [circles] - Common issues circle titles & icons.
 * @param {Boolean|Object} [contentsData] - Format contents data.
 * @param {Boolean|Array} data - Formatted metafield (of metaobjects) data.
 * @param {Boolean} [isPaddingLarge] - Whether to use larger padding styles.
 * @param {Boolean} [isTileButton] - If component should render each tile as
 * a button.
 * @param {String} namespace - Unique namespace (for overlay & aria IDs).
 * @param {Number} [numColumns] - Number of columns to include (desktop only).
 * @param {Boolean|Object} product - Canvas formatted product object.
 * @param {String} [subheading] - Subheading.
 * @param {String} [title] - Tile title.
 *
 */
import { mapActions, mapGetters } from 'vuex'

import Btn from '~global/btn/btn'
import Overlay from '~global/overlay/overlay'
import RteContent from '~async/rte-content/rte-content'

import IconCalendar from '~icons/general/calendar.svg'
import IconCircle30 from '~icons/general/circle-30.svg'
import IconCircle70 from '~icons/general/circle-70.svg'
import IconCircle100 from '~icons/general/circle-100.svg'
import IconKey from '~icons/general/key.svg'
import IconMagGlass from '~icons/general/magglass.svg'
import IconManual from '~icons/general/manual.svg'
import IconPhone from '~icons/general/phone.svg'
import IconPlay from '~icons/general/play.svg'
import IconSpanner from '~icons/general/spanner.svg'
import IconStar from '~icons/general/star.svg'
import IconTick from '~icons/general/tick.svg'
import IconVan from '~icons/general/van.svg'
import IconWallet from '~icons/general/wallet.svg'

export default {
  name: 'ProductDetails',

  components: {
    Btn,
    IconCalendar,
    IconCircle30,
    IconCircle70,
    IconCircle100,
    IconKey,
    IconMagGlass,
    IconManual,
    IconPhone,
    IconPlay,
    IconSpanner,
    IconStar,
    IconTick,
    IconVan,
    IconWallet,
    Overlay,
    RteContent,
  },

  props: {
    circles: {
      type: Array,
      default: () => [],
    },
    contentsData: {
      type: [Boolean, Object],
      default: false,
    },
    data: {
      type: [Boolean, Array],
      default: false,
    },
    isPaddingLarge: {
      type: Boolean,
      default: false,
    },
    isTileButton: {
      type: Boolean,
      default: false,
    },
    namespace: {
      type: String,
      default: '',
      required: true,
    },
    numColumns: {
      type: [Number, String],
      default: 2,
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
    subheading: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Compute if the compatibility information should be shown.
     * @returns {Boolean}
     */
    compatibility() {
      return this.title === this.$string('product.section_titles.more_information')
    },

    /**
     * Compute details component.
     * - `<section>` requires a heading element.
     * @returns {String}
     */
    detailsComponent() {
      return this.title ? 'section' : 'div'
    },

    /**
     * Compute local data.
     * - Adds compatibility drawer button.
     * @returns {Array<Object>}
     */
    localData() {
      if (this.compatibility) {
        const data = this.data ? [...this.data] : []

        data.push({
          heading: this.$string('product.tiles.all_vehicles'),
          compatibility: true,
          content: '',
        })

        return data
      }

      return this.data
    },
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openFinderFlow: 'garage/openFinderFlow',
      openOverlay: 'overlays/open',
    }),

    /**
     * Get progress circle information (icon & title).
     * - From Common Issues metaobject
     * @param {Number} index - index
     * @returns {String}
     */
    getCircleInfo(index) {
      return this.circles[index]
    },

    /**
     * Return name of icon component.
     * @param {String} icon - Icon name.
     * @returns {String}
     */
    getIcon(icon) {
      return `Icon${icon}`
    },

    /**
     * Compute progress icon based on value metafield.
     * @param {String} value - Low/Med/High metafield.
     * @returns {String}
     */
    getProgressIcon(value) {
      const sanitisedValue = value.toLowerCase()
      let icon = ''

      switch (sanitisedValue) {
        case 'low':
          icon = 'circle-30'
          break
        case 'medium':
          icon = 'circle-70'
          break
        case 'high':
          icon = 'circle-100'
          break
      }

      return icon
    },

    /**
     * Return screenreader friendly learn more string.
     * @param {Object} item - Metaobject data.
     * @returns {String}
     */
    learnMoreString(item) {
      return this.$string('product.tiles.learn_more_html')
        .replace('{{ title }}', item.heading)
    },

    /**
     * Compute if a product details overlay is active.
     * @param {Number|String} identifier - Overlay identifier (index or custom
     * string).
     * @returns {Boolean}
     */
    isActiveOverlay(identifier) {
      return this.getActiveOverlay?.namespace ===
        `productDetail${this.namespace}${identifier}`
    },

    /**
     * Handle opening specific drawer
     * @param {Number|String} identifier - Overlay identifier (index or custom
     * string).
     * @param {Object} item - Metaobject data.
     */
    handleOpenOverlay(identifier, item) {
      if (item?.compatibility) {
        this.openFinderFlow({ product: this.product, type: 'compatibility' })
        return
      }

      this.openOverlay({
        component: 'rte-content',
        ignoreDismissed: true,
        namespace: `productDetail${this.namespace}${identifier}`,
      })
    },
  },
}
</script>

<style lang="scss">
@import './product-details';
</style>
